/**
 * Product page: variant picker (E2), media gallery (E1), buy box (E3).
 *
 * Variant changes are resolved entirely client-side from the JSON the server
 * embedded at render time. That data is authoritative — it came from the same
 * Liquid render as the page — so there is no round trip and the picker feels
 * instant. Only the add-to-cart itself touches the network.
 */
(function () {
  'use strict';

  var V = window.Velocity;
  if (!V) return;

  /* =======================================================================
   * E2 — variant picker
   * ==================================================================== */
  class VariantPicker extends V.Element {
    init() {
      var dataEl = this.querySelector('[data-variant-data]');
      if (!dataEl) return;

      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;                       // malformed data must not break the page
      }

      this.inputs = Array.prototype.slice.call(
        this.querySelectorAll('.variant-picker__input')
      );
      this.optionCount = this.querySelectorAll('[data-option-index]').length;

      this.on(this, 'change', this.onChange.bind(this));

      // Back/forward must restore the variant that URL describes.
      this.on(window, 'popstate', this.onPopState.bind(this));

      // Seed the CURRENT history entry with the variant it represents.
      // Without this, a landing URL carrying no ?variant leaves the first
      // entry stateless, so going back from the first pick has nothing to
      // restore and the picker stays on the newer selection.
      var initial = this.findVariant(this.selectedOptions());
      if (initial) {
        window.history.replaceState(
          { variantId: initial.id },
          '',
          window.location.href
        );
      }

      this.updateAvailability();
    }

    selectedOptions() {
      var values = [];
      this.inputs.forEach(function (input) {
        if (input.checked) values[parseInt(input.dataset.optionIndex, 10)] = input.value;
      });
      return values;
    }

    findVariant(options) {
      return this.variants.filter(function (variant) {
        return options.every(function (value, i) {
          return value === undefined || variant.options[i] === value;
        });
      })[0];
    }

    onChange() {
      var options = this.selectedOptions();
      var variant = this.findVariant(options);

      this.updateAvailability();
      this.updateSelectedLabels();

      V.publish(V.EVENTS.variantChange, { variant: variant || null });

      if (variant) this.updateUrl(variant.id);
    }

    /**
     * Disable option values that cannot be reached from the current selection.
     *
     * Each value is evaluated by holding every OTHER option at its current
     * choice — so narrowing never traps the shopper in a dead end they cannot
     * click out of. A value with no available variant is marked sold out
     * rather than removed, because disappearing options are disorienting.
     */
    updateAvailability() {
      var self = this;

      this.inputs.forEach(function (input) {
        var index = parseInt(input.dataset.optionIndex, 10);
        var probe = self.selectedOptions();
        probe[index] = input.value;

        var matches = self.variants.filter(function (variant) {
          return probe.every(function (value, i) {
            return value === undefined || variant.options[i] === value;
          });
        });

        var exists = matches.length > 0;
        var available = matches.some(function (v) { return v.available; });

        var label = self.querySelector('label[for="' + input.id + '"]');
        if (!label) return;

        label.classList.toggle('is-unavailable', !available);
        label.classList.toggle('is-nonexistent', !exists);

        // Kept focusable and selectable on purpose: a shopper must be able to
        // pick a sold-out colour to see which sizes it has. The buy button is
        // what goes disabled.
        input.setAttribute('aria-describedby', available ? '' : 'variant-unavailable-note');
      });
    }

    updateSelectedLabels() {
      var options = this.selectedOptions();
      var self = this;
      options.forEach(function (value, i) {
        var el = self.querySelector('[data-selected-value="' + i + '"]');
        if (el) el.textContent = value;
      });
    }

    updateUrl(variantId) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      // replaceState on the first change would lose the entry point, so push:
      // the acceptance criterion is that back/forward restores the variant.
      window.history.pushState({ variantId: variantId }, '', url.toString());
    }

    onPopState(event) {
      var id = event.state && event.state.variantId;
      if (!id) {
        id = new URL(window.location.href).searchParams.get('variant');
      }
      if (!id) return;

      var variant = this.variants.filter(function (v) {
        return String(v.id) === String(id);
      })[0];
      if (!variant) return;

      var self = this;
      variant.options.forEach(function (value, i) {
        self.inputs.forEach(function (input) {
          if (parseInt(input.dataset.optionIndex, 10) === i && input.value === value) {
            input.checked = true;
          }
        });
      });

      this.updateAvailability();
      this.updateSelectedLabels();
      V.publish(V.EVENTS.variantChange, { variant: variant, fromHistory: true });
    }
  }

  /* =======================================================================
   * E1 — media gallery
   *
   * Scrolling is pure CSS scroll-snap, which is why the mobile carousel stays
   * at 60fps: no scroll listener drives the animation. JS only syncs the
   * thumbnail state and jumps to a variant's image.
   * ==================================================================== */
  class ProductGallery extends V.Element {
    init() {
      this.viewport = this.querySelector('[data-gallery-viewport]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-media-id]'));
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-thumb]'));

      var self = this;
      this.thumbs.forEach(function (thumb) {
        self.on(thumb, 'click', function (e) {
          e.preventDefault();
          self.goTo(thumb.dataset.thumb, true);
        });
      });

      // Keep thumbnails in sync when the shopper swipes instead of tapping.
      if (this.viewport && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) self.setActive(entry.target.dataset.mediaId);
          });
        }, { root: this.viewport, threshold: 0.6 });

        this.slides.forEach(function (slide) { self.observer.observe(slide); });
      }

      this.listen(V.EVENTS.variantChange, function (data) {
        if (data.variant && data.variant.featured_media_id) {
          self.goTo(String(data.variant.featured_media_id), false);
        }
      });

      this.initZoom();
    }

    destroy() {
      if (this.observer) this.observer.disconnect();
    }

    goTo(mediaId, fromClick) {
      var slide = this.slides.filter(function (s) {
        return s.dataset.mediaId === String(mediaId);
      })[0];
      if (!slide || !this.viewport) return;

      this.viewport.scrollTo({
        left: slide.offsetLeft - this.viewport.offsetLeft,
        behavior: (fromClick && !V.prefersReducedMotion()) ? 'smooth' : 'auto'
      });
      this.setActive(mediaId);
    }

    setActive(mediaId) {
      this.thumbs.forEach(function (thumb) {
        var isActive = thumb.dataset.thumb === String(mediaId);
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    /**
     * Zoom. A real <button> per slide rather than a hover-only effect, so it
     * is reachable by keyboard and usable on touch (E1 acceptance criterion).
     */
    initZoom() {
      var self = this;
      Array.prototype.forEach.call(this.querySelectorAll('[data-zoom-toggle]'), function (btn) {
        self.on(btn, 'click', function () {
          var figure = btn.closest('[data-media-id]');
          var zoomed = figure.classList.toggle('is-zoomed');
          btn.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
        });
      });
    }
  }

  /* =======================================================================
   * E3 — buy box
   * ==================================================================== */
  class ProductForm extends V.Element {
    init() {
      this.form = this.querySelector('form');
      this.idInput = this.querySelector('[data-variant-id-input]');
      this.button = this.querySelector('[data-add-to-cart]');
      this.buttonText = this.querySelector('[data-add-to-cart-text]');
      this.priceEl = this.querySelector('[data-price]');
      this.notify = this.querySelector('[data-notify]');
      this.qtyInput = this.querySelector('[data-qty-input]');

      if (this.form) this.on(this.form, 'submit', this.onSubmit.bind(this));

      this.listen(V.EVENTS.variantChange, this.onVariantChange.bind(this));
    }

    onVariantChange(data) {
      var variant = data.variant;

      if (!variant) {
        this.setUnavailable(V.strings.unavailable);
        return;
      }

      if (this.idInput) this.idInput.value = variant.id;

      if (this.priceEl) {
        var html = '<span class="price__current">' + variant.price_formatted + '</span>';
        if (variant.compare_at_price > variant.price) {
          html =
            '<s class="price__was">' + variant.compare_at_formatted + '</s>' + html;
          this.priceEl.classList.add('price--on-sale');
        } else {
          this.priceEl.classList.remove('price--on-sale');
        }
        this.priceEl.innerHTML = html;
      }

      // Cap the quantity field to what is actually purchasable.
      if (this.qtyInput) {
        if (variant.inventory_management && variant.inventory_policy === 'deny') {
          this.qtyInput.max = variant.inventory_quantity;
          if (parseInt(this.qtyInput.value, 10) > variant.inventory_quantity) {
            this.qtyInput.value = Math.max(1, variant.inventory_quantity);
          }
        } else {
          this.qtyInput.removeAttribute('max');
        }
      }

      if (variant.available) this.setAvailable();
      else this.setUnavailable(V.strings.soldOut);
    }

    setAvailable() {
      if (this.button) {
        this.button.disabled = false;
        this.button.removeAttribute('aria-disabled');
      }
      if (this.buttonText) this.buttonText.textContent = V.strings.addToCart;
      if (this.notify) this.notify.hidden = true;
    }

    setUnavailable(message) {
      if (this.button) {
        this.button.disabled = true;
        this.button.setAttribute('aria-disabled', 'true');
      }
      if (this.buttonText) this.buttonText.textContent = message;
      // Only offer the notify capture for a real sold-out variant, not for a
      // combination that does not exist at all.
      if (this.notify) this.notify.hidden = message !== V.strings.soldOut;
    }

    onSubmit(e) {
      e.preventDefault();
      if (!this.button || this.button.disabled) return;

      var self = this;
      this.button.setAttribute('aria-busy', 'true');

      V.addToCart(new FormData(this.form))
        .catch(function (err) {
          if (self.buttonText) self.buttonText.textContent = err.message || V.strings.cartError;
        })
        .then(function () {
          self.button.removeAttribute('aria-busy');
          if (self.buttonText) self.buttonText.textContent = V.strings.addToCart;
        });
    }
  }

  /* =======================================================================
   * E3 — sticky mobile add-to-cart
   *
   * The bar is position:fixed and translated out of view, so revealing it
   * cannot shift any content (zero CLS). It mirrors the real button rather
   * than duplicating the form.
   * ==================================================================== */
  class StickyAtc extends V.Element {
    init() {
      var target = document.querySelector('[data-add-to-cart]');
      if (!target || !('IntersectionObserver' in window)) return;

      var self = this;
      this.button = this.querySelector('[data-sticky-add]');

      if (this.button) {
        this.on(this.button, 'click', function () {
          // Delegate to the real button so there is one add-to-cart path.
          target.click();
        });
      }

      this.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          self.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      }, { threshold: 0 });

      this.observer.observe(target);

      this.listen(V.EVENTS.variantChange, function (data) {
        if (!self.button) return;
        var available = data.variant && data.variant.available;
        self.button.disabled = !available;
        var label = self.querySelector('[data-sticky-price]');
        if (label && data.variant) label.textContent = data.variant.price_formatted;
      });
    }

    destroy() {
      if (this.observer) this.observer.disconnect();
    }
  }

  /* =======================================================================
   * Quantity stepper
   * ==================================================================== */
  class QuantityInput extends V.Element {
    init() {
      this.input = this.querySelector('[data-qty-input]');
      if (!this.input) return;

      var self = this;
      this.on(this, 'click', function (e) {
        var down = e.target.closest('[data-qty-down]');
        var up = e.target.closest('[data-qty-up]');
        if (!down && !up) return;
        e.preventDefault();

        var value = parseInt(self.input.value, 10) || 1;
        var min = parseInt(self.input.min, 10) || 1;
        var max = parseInt(self.input.max, 10);

        value += up ? 1 : -1;
        if (value < min) value = min;
        if (!isNaN(max) && value > max) value = max;

        self.input.value = value;
        self.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  V.define('variant-picker', VariantPicker);
  V.define('product-gallery', ProductGallery);
  V.define('product-form', ProductForm);
  V.define('sticky-atc', StickyAtc);
  V.define('quantity-input', QuantityInput);
})();
